import { selectCurrentTeam } from 'containers/app/appSlice';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppSelector } from 'hooks/typedHooks';
import { isPermissionDenied } from 'utils/apiUtils';
import { useLazyGetInvoicesV2Query } from '../api';
import { InvoiceV2ColumnInfo, InvoiceV2ListItem, StatusBasedCount } from '../invoices.model';
import { formatInvoiceFilterParamsForAPI } from '../utils';
import { INVOICE_TAB_STATUS } from '../constants';
import DateUtils from 'utils/dateUtils';

export interface InvoiceV2QueryParams {
  searchKey?: string;
  searchValue?: string;
  sortBy?: string;
  sortOrder?: string;
  focusedColumns?: string[];
  customerId?: string[];
  createdAtFrom?: string;
  createdAtTo?: string;
  createdBy?: string[];
  dueDateFrom?: string;
  dueDateTo?: string;
  totalAmount?: string;
  refundStatus?: string;
  disputeStatus?: string;
  reminderStatus?: string;
  jobStatus?: string;
  estimateStatus?: string;
  groupingStatus?: INVOICE_TAB_STATUS;
  invoiceStatus?: string[];
  paymentMethods?: string[];
  tipStatus?: string;
  limit?: number;
}

export const initialInvoiceV2QueryParamsState: InvoiceV2QueryParams = {
  sortBy: '',
  sortOrder: ''
};

export const usePaginatedGetInvoicesV2Query = (limit = 50) => {
  const [list, setList] = useState<InvoiceV2ListItem[]>([]);
  const [queryParams, setQueryParams] = useState<InvoiceV2QueryParams>(initialInvoiceV2QueryParamsState);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isError, setIsError] = useState<boolean>(false);
  const [totalListCount, setTotalListCount] = useState<number>(0);
  const [statusBasedCount, setStatusBasedCount] = useState<StatusBasedCount | null>(null);
  const [hasPermissionsToView, setHasPermissionsToView] = useState(true);
  const [columnConfig, setColumnConfig] = useState<InvoiceV2ColumnInfo[]>([]);
  const page = useRef(1);
  const [getInvoicesV2Query, invoicesState] = useLazyGetInvoicesV2Query();
  const currentTeam = useAppSelector(selectCurrentTeam);
  const isInitListCall: boolean = page.current === 1;
  const queryParamsRef = useRef(queryParams);

  const timezone = DateUtils.getTimezoneLongName();

  useEffect(() => {
    queryParamsRef.current = queryParams;
  }, [queryParams]);

  const queryParamsKey = useMemo(() => JSON.stringify(queryParams), [queryParams]);
  const hasSetInitialGroupingStatusRef = useRef(false);

  useEffect(() => {
    if (!invoicesState?.isFetching) {
      setHasPermissionsToView(true);

      if (invoicesState?.data) {
        const { invoices, columnInfo, filteredCount, statusBasedCount: statusBasedCountData } = invoicesState.data;
        setTotalListCount(filteredCount);
        setStatusBasedCount(statusBasedCountData || null);
        setList(invoices || []);

        if (columnInfo && Array.isArray(columnInfo)) {
          const sortedColumnInfo = [...columnInfo].sort((a, b) => a.priority - b.priority);
          setColumnConfig(sortedColumnInfo);
        } else {
          setColumnConfig(columnInfo || []);
        }
      }

      setIsLoading(false);
    }
  }, [invoicesState?.isFetching]);

  useEffect(() => {
    if (!invoicesState?.isFetching && invoicesState?.isError) {
      setIsLoading(false);
      if (isPermissionDenied(invoicesState.error?.['data']?.code, invoicesState.error?.['data']?.message)) {
        setHasPermissionsToView(false);
      }
      setIsError(true);
      setList([]);
      setTotalListCount(0);
    }
  }, [invoicesState?.isError]);

  const fetchData = useCallback(
    (currentPage: number) => {
      setIsLoading(true);
      setIsError(false);
      page.current = currentPage;
      const currentQueryParams = queryParamsRef.current;
      const finalQueryParams = formatInvoiceFilterParamsForAPI(currentQueryParams);
      const currentLimit = currentQueryParams.limit ?? limit;
      if (currentTeam?.id) {
        try {
          getInvoicesV2Query({
            teamId: currentTeam.id.toString(),
            limit: currentLimit,
            page: page.current - 1,
            timezone: timezone,
            source: 'WEB',
            ...finalQueryParams
          }).unwrap();
        } catch (err) {
          setIsError(true);
          setList([]);
          setTotalListCount(0);
        }
      } else {
        setList([]);
        setTotalListCount(0);
      }
    },
    [currentTeam?.id, limit, getInvoicesV2Query]
  );

  useEffect(() => {
    if (!hasSetInitialGroupingStatusRef.current && !queryParams.groupingStatus) {
      setQueryParams(prevParams => ({
        ...prevParams,
        groupingStatus: INVOICE_TAB_STATUS.ALL_INVOICES
      }));
      hasSetInitialGroupingStatusRef.current = true;
      return;
    }

    setList([]);
    fetchData(1);
  }, [queryParamsKey, fetchData]);

  const resetQuery = useCallback(() => {
    setQueryParams(initialInvoiceV2QueryParamsState);
    setStatusBasedCount(null);
    setList([]);
    setTotalListCount(0);
  }, []);

  return {
    isLoading,
    isFetching: invoicesState?.isFetching,
    totalListCount,
    statusBasedCount,
    list,
    isError,
    fetchData,
    resetQuery,
    setQueryParams,
    queryParams,
    isInitListCall,
    isLoadingSuccess: invoicesState.isSuccess,
    hasPermissionsToView,
    page: page.current,
    columnConfig
  };
};
